/**
 * Cognito's SRP handshake, with no dependencies.
 *
 * ADR-0006 chose Cognito and the user pool client enables only
 * `ALLOW_USER_SRP_AUTH`, so the password never crosses the wire -- the client
 * proves knowledge of it instead. That proof is this file.
 *
 * Hand-rolled rather than taken from `amazon-cognito-identity-js`, which costs
 * 1.4 MB unpacked and five transitive dependencies -- an exact-pinned
 * `buffer@4.9.2`, a cookie library, a `fetch` polyfill and a JavaScript SHA-256 --
 * four of which exist to support environments this project does not target. Every
 * primitive needed here is native: `BigInt` for the modular arithmetic, Web Crypto
 * for SHA-256 and HMAC, `fetch` for the calls.
 *
 * The trade is that this is authentication code we maintain. Two things make that
 * tolerable: SRP fails closed and completely -- any error in `k`, `x`, `u`, the
 * derived key or the timestamp produces a signature the server rejects, so there
 * is no partially-working state to miss -- and it is verified against the real
 * user pool rather than against a mock.
 *
 * Reference: RFC 5054, and Cognito's particular variant of it.
 */

/**
 * The 3072-bit MODP group from RFC 3526, which RFC 5054 adopts and Cognito uses.
 *
 * Verified before use: 3072 bits, prime, and a safe prime -- `(N-1)/2` is also
 * prime. A single mistyped digit would be overwhelmingly unlikely to preserve
 * both properties, which is the only practical way to check a constant this long.
 */
const N_HEX =
  'FFFFFFFFFFFFFFFFC90FDAA22168C234C4C6628B80DC1CD129024E088A67CC74' +
  '020BBEA63B139B22514A08798E3404DDEF9519B3CD3A431B302B0A6DF25F1437' +
  '4FE1356D6D51C245E485B576625E7EC6F44C42E9A637ED6B0BFF5CB6F406B7ED' +
  'EE386BFB5A899FA5AE9F24117C4B1FE649286651ECE45B3DC2007CB8A163BF05' +
  '98DA48361C55D39A69163FA8FD24CF5F83655D23DCA3AD961C62F356208552BB' +
  '9ED529077096966D670C354E4ABC9804F1746C08CA18217C32905E462E36CE3B' +
  'E39E772C180E86039B2783A2EC07A28FB5C55DF06F4C52C9DE2BCBF695581718' +
  '3995497CEA956AE515D2261898FA051015728E5A8AAAC42DAD33170D04507A33' +
  'A85521ABDF1CBA64ECFB850458DBEF0A8AEA71575D060C7DB3970F85A6E1E4C7' +
  'ABF5AE8CDB0933D71E8C94E04A25619DCEE3D2261AD2EE6BF12FFA06D98A0864' +
  'D87602733EC86A64521F2B18177B200CBBE117577A615D6C770988C0BAD946E2' +
  '08E24FA074E5AB3143DB5BFCE0FD108E4B82D120A93AD2CAFFFFFFFFFFFFFFFF';

export const N = BigInt(`0x${N_HEX}`);
export const G = 2n;

const INFO_BITS = 'Caldera Derived Key';
const DERIVED_KEY_BYTES = 16;

const utf8 = new TextEncoder();

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

export function hexToBytes(hex: string): Uint8Array {
  const normalized = hex.length % 2 === 1 ? `0${hex}` : hex;
  const bytes = new Uint8Array(normalized.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(normalized.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

export const bytesToHex = (bytes: Uint8Array): string =>
  Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');

/**
 * Hex with an even length, and a leading `00` when the top bit is set.
 *
 * SRP hashes these values as unsigned big-endian integers, so a leading byte of
 * `0x80` or above has to be distinguished from a negative number. Matching this
 * exactly matters: it decides the bytes that go into `k`, `u` and `x`.
 */
export function padHex(value: bigint): string {
  let hex = value.toString(16);
  if (hex.length % 2 === 1) hex = `0${hex}`;
  else if ('89abcdef'.includes(hex[0]!)) hex = `00${hex}`;
  return hex;
}

/** Square-and-multiply: `base ** exponent` would be astronomically large. */
export function modPow(base: bigint, exponent: bigint, modulus: bigint): bigint {
  let result = 1n;
  let b = ((base % modulus) + modulus) % modulus;
  let e = exponent;
  while (e > 0n) {
    if (e & 1n) result = (result * b) % modulus;
    b = (b * b) % modulus;
    e >>= 1n;
  }
  return result;
}

const mod = (value: bigint, modulus: bigint): bigint => ((value % modulus) + modulus) % modulus;

async function sha256(bytes: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.digest('SHA-256', bytes as BufferSource));
}

const sha256Hex = async (bytes: Uint8Array): Promise<string> => bytesToHex(await sha256(bytes));

async function hmacSha256(key: Uint8Array, message: Uint8Array): Promise<Uint8Array> {
  const imported = await crypto.subtle.importKey(
    'raw',
    key as BufferSource,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  return new Uint8Array(await crypto.subtle.sign('HMAC', imported, message as BufferSource));
}

/**
 * Base64 of arbitrary bytes.
 *
 * `atob`/`btoa` map one byte to one code unit, which is exactly right here and
 * exactly wrong for text -- decoding a UTF-8 payload through `atob` yields latin1
 * and corrupts anything non-ASCII. These are bytes, so this is the correct use.
 */
const bytesToBase64 = (bytes: Uint8Array): string =>
  btoa(Array.from(bytes, (b) => String.fromCharCode(b)).join(''));

const base64ToBytes = (value: string): Uint8Array =>
  Uint8Array.from(atob(value), (c) => c.charCodeAt(0));

const concat = (...parts: Uint8Array[]): Uint8Array => {
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
};

// ---------------------------------------------------------------------------
// The handshake
// ---------------------------------------------------------------------------

export interface Ephemeral {
  /** The client's secret exponent. Never leaves the device. */
  readonly a: bigint;
  /** `g^a mod N`, sent to the server as `SRP_A`. */
  readonly A: bigint;
}

/**
 * Starts a handshake.
 *
 * 256 bits of randomness for the ephemeral secret, which is the usual lower bound
 * for SRP. `randomBytes` is injectable so a test can pin it; nothing else should
 * pass it.
 */
export function startHandshake(randomBytes?: Uint8Array): Ephemeral {
  const bytes = randomBytes ?? crypto.getRandomValues(new Uint8Array(32));
  const a = mod(BigInt(`0x${bytesToHex(bytes)}`), N);
  return { a, A: modPow(G, a, N) };
}

/** `k = H(PAD(N) | PAD(g))`, constant for the group but cheap enough to derive. */
const multiplier = async (): Promise<bigint> =>
  BigInt(`0x${await sha256Hex(hexToBytes(padHex(N) + padHex(G)))}`);

/**
 * The timestamp that goes into the signed message.
 *
 * Format is `EEE MMM d HH:mm:ss UTC yyyy` in UTC, and the day of the month is
 * **not** zero-padded while the time fields are. That asymmetry is the classic
 * way this handshake fails: every other value can be right and the server still
 * rejects the signature, with no indication why.
 */
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

export function cognitoTimestamp(at: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  const time = `${pad(at.getUTCHours())}:${pad(at.getUTCMinutes())}:${pad(at.getUTCSeconds())}`;
  return (
    `${WEEKDAYS[at.getUTCDay()]} ${MONTHS[at.getUTCMonth()]} ${at.getUTCDate()} ` +
    `${time} UTC ${at.getUTCFullYear()}`
  );
}

/**
 * Derives the shared session key.
 *
 * `S = (B - k * g^x) ^ (a + u * x) mod N`, then a single-block HKDF expansion
 * truncated to 16 bytes, which is Cognito's variant.
 */
export async function deriveKey(params: {
  poolName: string;
  userIdForSrp: string;
  password: string;
  saltHex: string;
  srpBHex: string;
  ephemeral: Ephemeral;
}): Promise<Uint8Array> {
  const { poolName, userIdForSrp, password, saltHex, srpBHex, ephemeral } = params;

  const B = BigInt(`0x${srpBHex}`);
  // Both are protocol aborts rather than assertions: a server that sends either
  // is not one this client should finish a handshake with.
  if (mod(B, N) === 0n) throw new Error('SRP: server sent B ≡ 0 mod N');

  const u = BigInt(`0x${await sha256Hex(hexToBytes(padHex(ephemeral.A) + padHex(B)))}`);
  if (u === 0n) throw new Error('SRP: u is zero');

  const k = await multiplier();

  // The salt goes through BigInt, which drops leading zero bytes. That is what
  // Cognito's own implementations do, so matching it is required rather than tidy.
  const salt = BigInt(`0x${saltHex}`);
  const identityHash = await sha256Hex(utf8.encode(`${poolName}${userIdForSrp}:${password}`));
  const x = BigInt(`0x${await sha256Hex(hexToBytes(padHex(salt) + identityHash))}`);

  const S = modPow(mod(B - k * modPow(G, x, N), N), ephemeral.a + u * x, N);

  // HKDF-Expand with one block: extract with u as the salt, expand with the
  // fixed info string, keep the first 16 bytes.
  const prk = await hmacSha256(hexToBytes(padHex(u)), hexToBytes(padHex(S)));
  const expanded = await hmacSha256(prk, concat(utf8.encode(INFO_BITS), Uint8Array.of(1)));
  return expanded.slice(0, DERIVED_KEY_BYTES);
}

export interface PasswordClaim {
  readonly signature: string;
  readonly timestamp: string;
}

/**
 * The proof sent back as `PASSWORD_CLAIM_SIGNATURE`.
 *
 * Signs `poolName | userIdForSrp | secretBlock | timestamp` with the derived key.
 * The secret block is base64 of opaque bytes and is signed as bytes, not as text.
 */
export async function passwordClaim(params: {
  poolName: string;
  userIdForSrp: string;
  password: string;
  saltHex: string;
  srpBHex: string;
  secretBlock: string;
  ephemeral: Ephemeral;
  at?: Date;
}): Promise<PasswordClaim> {
  const key = await deriveKey(params);
  const timestamp = cognitoTimestamp(params.at ?? new Date());

  const message = concat(
    utf8.encode(params.poolName),
    utf8.encode(params.userIdForSrp),
    base64ToBytes(params.secretBlock),
    utf8.encode(timestamp),
  );

  return { signature: bytesToBase64(await hmacSha256(key, message)), timestamp };
}

/** `us-east-1_AbC123` identifies the pool; only the part after the underscore is hashed. */
export function poolNameFrom(userPoolId: string): string {
  const name = userPoolId.split('_')[1];
  if (name === undefined || name === '') {
    throw new Error(`Malformed user pool id: "${userPoolId}"`);
  }
  return name;
}
